import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useAdminApi } from "@/lib/api";

const KEEP = ["paid", "payment_submitted", "confirmed", "completed"];
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);
const dmy = (d) => format(new Date(`${d}T00:00:00`), "dd-MM-yyyy");

function summarise(items) {
  const byType = {};
  const byDay = {};
  let total = 0;
  let unverified = 0;
  items.forEach((b) => {
    total += b.amount;
    if (b.status === "payment_submitted") unverified += b.amount;
    byType[b.service_label] = byType[b.service_label] || { count: 0, amount: 0 };
    byType[b.service_label].count += 1;
    byType[b.service_label].amount += b.amount;
    byDay[b.date] = byDay[b.date] || { count: 0, amount: 0 };
    byDay[b.date].count += 1;
    byDay[b.date].amount += b.amount;
  });
  return {
    total, unverified, count: items.length,
    types: Object.entries(byType).sort((a, b) => b[1].amount - a[1].amount),
    days: Object.entries(byDay).sort((a, b) => a[0].localeCompare(b[0])),
  };
}

const tableHtml = (head, rows) =>
  `<table><thead><tr>${head.map((h) => `<th>${h}</th>`).join("")}</tr></thead><tbody>${rows
    .map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join("")}</tr>`).join("")}</tbody></table>`;

function printReport(title, s) {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(`<html><head><title>${esc(title)}</title><style>
    body{font-family:Arial,sans-serif;margin:24px;color:#111}h1{font-size:18px;margin:0 0 6px}h2{font-size:14px;margin:18px 0 6px}
    p{margin:2px 0;font-size:12px}table{border-collapse:collapse;width:100%;font-size:12px}th,td{border:1px solid #999;padding:5px 6px;text-align:left}
    th{background:#eee}@page{size:A4;margin:14mm}</style></head><body>
    <h1>${esc(title)}</h1>
    <p>Total consultations: <b>${s.count}</b></p><p>Total earnings: <b>Rs ${s.total}</b></p>
    <p>Of which UPI not yet verified: Rs ${s.unverified}</p>
    <h2>By type of help</h2>${tableHtml(["Type of help", "Consultations", "Rs"], s.types.map(([k, v]) => [k, v.count, v.amount]))}
    <h2>By day</h2>${tableHtml(["Date", "Consultations", "Rs"], s.days.map(([k, v]) => [dmy(k), v.count, v.amount]))}
    <script>window.onload=function(){window.print()}</script></body></html>`);
  w.document.close();
}

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-black/20 p-4" data-testid={testId}>
    <p className="text-xs text-emerald-100/60">{label}</p>
    <p className="mt-1 font-display text-2xl text-white">{value}</p>
  </div>
);

const MiniTable = ({ head, rows, testId }) => (
  <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid={testId}>
    <Table>
      <TableHeader><TableRow>{head.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
      <TableBody>{rows.map((r) => <TableRow key={r[0]}>{r.map((c, j) => <TableCell key={j}>{c}</TableCell>)}</TableRow>)}</TableBody>
    </Table>
  </div>
);

export default function MonthlyReportTab() {
  const call = useAdminApi();
  const [month, setMonth] = useState(format(new Date(), "yyyy-MM"));
  const [items, setItems] = useState(null);

  const loadMonth = useCallback(() => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    setItems(null);
    call("get", "/admin/bookings", null, { params: { month } }).then((l) => setItems(l.filter((b) => KEEP.includes(b.status))));
  }, [call, month]);
  useEffect(() => {
    loadMonth();
  }, [loadMonth]);

  const s = useMemo(() => summarise(items || []), [items]);
  const title = `Joyful Minds - Monthly report ${/^\d{4}-\d{2}$/.test(month) ? format(new Date(`${month}-01T00:00:00`), "MMMM yyyy") : ""}`;

  return (
    <div className="space-y-5" data-testid="admin-monthly-report">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" data-testid="report-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48 bg-black/20" />
        <Button data-testid="report-print-btn" disabled={!items?.length} onClick={() => printReport(title, s)} variant="secondary" className="rounded-full">
          <Printer className="mr-2 h-4 w-4" /> Print report
        </Button>
      </div>
      {!items && <div className="h-24 animate-pulse rounded-xl bg-white/5" />}
      {items && (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Total consultations" value={s.count} testId="report-total-count" />
            <Stat label="Total earnings" value={`Rs ${s.total}`} testId="report-total-amount" />
            <Stat label="UPI not yet verified" value={`Rs ${s.unverified}`} testId="report-unverified-amount" />
          </div>
          {items.length === 0 ? (
            <p className="py-6 text-center text-sm text-emerald-100/60" data-testid="report-empty">No paid consultations in this month.</p>
          ) : (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-2">
                <p className="font-display text-lg text-white">By type of help</p>
                <MiniTable testId="report-by-type" head={["Type of help", "Consultations", "Rs"]} rows={s.types.map(([k, v]) => [k, v.count, v.amount])} />
              </div>
              <div className="space-y-2">
                <p className="font-display text-lg text-white">By day</p>
                <MiniTable testId="report-by-day" head={["Date", "Consultations", "Rs"]} rows={s.days.map(([k, v]) => [dmy(k), v.count, v.amount])} />
              </div>
            </div>
          )}
          <p className="text-xs text-emerald-100/50">Includes paid online, UPI submitted, verified and completed consultations. Cancelled and unpaid bookings are not counted.</p>
        </>
      )}
    </div>
  );
}
